<script setup>
import { inject, ref } from 'vue';
import { Link } from '@inertiajs/vue3';
import { TabPanel } from '@headlessui/vue';
import { ArrowLongRightIcon } from '@heroicons/vue/24/outline';
import { UserIcon } from '@heroicons/vue/20/solid';
import ShowMore from '../../../Components/ShowMore.vue';
import Tag from '../../../Components/Tag.vue';
import ContributorModal from './ContributorModal.vue';
import CooperationModal from './CooperationModal.vue';
import { useGetLocalizedDateTime } from '@/Composables/localizedDateTime.js';

defineEmits([
    'showAllContributors',
    'showAllCooperations',
]);

const programPackage = inject('programPackage');

const mainSponsor = programPackage.cooperations.mainSponsoring
    ? programPackage.cooperations.mainSponsoring[0]
    : null;

const displayedContributor = ref(null);
const displayedCooperation = ref(null);

const overviewContributors = programPackage.contributions.filter(contributor => contributor.display_on_overview_tab);

const taxRate = 19;

const currency = number => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(number);
</script>

<template>
    <TabPanel class="focus-visible:outline-deep-teal-700">
        <div class="grid grid-cols-1 lg:grid-cols-[2fr_1fr] lg:gap-x-8 gap-y-8 lg:gap-y-0">
            <div class="space-y-8">
                <div class="space-y-6">
                    <h2 class="text-2xl font-semibold text-deep-teal-700">
                        {{ $t('eventDetails.tabs.general') }}
                    </h2>

                    <dl
                        class="grid grid-cols-1 md:grid-cols-[repeat(2,_minmax(min-content,_max-content))] gap-y-4 md:gap-x-4 lg:gap-x-8 text-gray-700"
                    >
                        <dt
                            v-if="programPackage.teaser"
                            class="font-semibold"
                        >
                            {{ $t('eventDetails.headline.teaser') }}
                        </dt>
                        <dd v-if="programPackage.teaser">
                            <p>
                                {{ programPackage.teaser }}
                            </p>
                        </dd>

                        <dt
                            v-if="programPackage.event_business_areas"
                            class="font-semibold"
                        >
                            {{ $t('eventDetails.headline.eventBusinessAreas') }}
                        </dt>
                        <dd v-if="programPackage.event_business_areas">
                            <ul class="flex flex-wrap -mx-1.5 -my-1 list-none">
                                <Tag
                                    v-for="businessArea in programPackage.event_business_areas"
                                    :key="businessArea.name"
                                    as="li"
                                >
                                    {{ businessArea.name }}
                                </Tag>
                            </ul>
                        </dd>

                        <dt
                            v-if="programPackage.event_topics"
                            class="font-semibold"
                        >
                            {{ $t('eventDetails.headline.eventTopics') }}
                        </dt>
                        <dd v-if="programPackage.event_topics">
                            <ul class="flex flex-wrap -mx-1.5 -my-1 list-none">
                                <Tag
                                    v-for="topic in programPackage.event_topics"
                                    :key="topic.name"
                                    as="li"
                                >
                                    {{ topic.name }}
                                </Tag>
                            </ul>
                        </dd>

                        <dt
                            v-if="programPackage.tags.length > 0"
                            class="font-semibold"
                        >
                            {{ $t('eventDetails.headline.tags') }}
                        </dt>
                        <dd v-if="programPackage.tags.length > 0">
                            <ul class="flex flex-wrap -mx-1.5 -my-1 list-none">
                                <Tag
                                    v-for="tag in programPackage.tags"
                                    :key="tag"
                                    as="li"
                                    type="muted"
                                >
                                    {{ tag }}
                                </Tag>
                            </ul>
                        </dd>

                        <dt class="font-semibold">
                            {{ $t('eventDetails.headline.date') }}
                        </dt>
                        <dd class="space-y-3">
                            <div
                                class="flex flex-col space-y-3 font-semibold leading-none"
                            >
                                <div>
                                    <time :datetime="programPackage.start_date">
                                        {{ useGetLocalizedDateTime(programPackage.start_date) }}
                                    </time>
                                    {{ $t('eventDetails.date.to') }}
                                </div>
                                <div>
                                    <time :datetime="programPackage.end_date">
                                        {{ useGetLocalizedDateTime(programPackage.end_date) }}
                                    </time>
                                </div>
                            </div>

                            <div class="font-light text-xs/none">
                                {{ $t('eventDetails.date.timezone') }}
                            </div>
                        </dd>

                        <dt
                            v-if="programPackage.location"
                            class="font-semibold"
                        >
                            {{ $t('eventDetails.headline.location') }}
                        </dt>
                        <dd
                            v-if="programPackage.location"
                        >
                            <div class="space-y-2">
                                <div class="font-medium">
                                    {{ programPackage.location.display_name }}
                                </div>

                                <div>
                                    {{ programPackage.location.street_house_number }}
                                    <template v-if="programPackage.location.addition">
                                        ({{ programPackage.location.addition }})
                                    </template><br>
                                    {{ programPackage.location.zip_code }} {{ programPackage.location.city }}<br>
                                    {{ programPackage.location.country_name }}
                                </div>

                                <a
                                    v-if="programPackage.location.directions_url"
                                    :href="programPackage.location.directions_url"
                                    class="inline-block px-3 py-1 mt-4 text-sm font-bold text-gray-700 transition-colors border-2 bg-golden-fizz-500 border-golden-fizz-500 rounded-3xl hover:bg-golden-fizz-600 hover:border-golden-fizz-600 focus:outline-none focus:bg-golden-fizz-600 focus:border-golden-fizz-600"
                                    target="_blank"
                                    rel="noreferrer noopener"
                                >
                                    {{ $t('eventDetails.label.directionsUrl') }}
                                </a>
                            </div>
                        </dd>

                        <dt class="font-semibold">
                            {{ $t('eventDetails.headline.price') }}
                        </dt>
                        <dd>
                            <div
                                v-if="programPackage.event_format"
                                class="flex flex-col sm:flex-row space-x-0 sm:space-x-2 "
                            >
                                <div>
                                    {{ currency(programPackage.event_format.price / 100) }} | {{ $t('eventDetails.priceNormal') }}
                                </div>
                                <div>
                                    <span class="text-gray-700 text-sm font-light leading-4">({{ $t('eventDetails.grossPrice.text') }} {{ currency(programPackage.event_format.price * (1+ (taxRate / 100)) / 100) }})</span>
                                </div>
                            </div>

                            <div
                                v-if="programPackage.event_format"
                                class="flex flex-col sm:flex-row space-x-0 sm:space-x-2 "
                            >
                                <div>
                                    {{ currency(programPackage.event_format.price_members / 100) }} | {{ $t('eventDetails.priceMember') }}
                                </div>
                                <div>
                                    <span class="text-gray-700 text-sm font-light leading-4">({{ $t('eventDetails.grossPrice.text') }} {{ currency(programPackage.event_format.price_members * (1+ (taxRate / 100)) / 100) }})</span>
                                </div>
                            </div>

                            <component
                                :is="programPackage.is_checkoutable ? Link : 'button'"
                                :disabled="!programPackage.is_checkoutable"
                                :href="programPackage.is_checkoutable ? route('event-checkout.show', { eventFormatCategory: programPackage.event_format.event_format_category.slug, programPackage: programPackage.slug }) : null"
                                class="inline-block px-3 py-1 mt-4 text-sm font-bold transition-colors border-2 rounded-3xl"
                                :class="[!programPackage.is_checkoutable ? 'text-gray-400 bg-golden-fizz-200 border-golden-fizz-200 hover:bg-golden-fizz-200 hover:border-golden-fizz-200' : 'text-gray-700 bg-golden-fizz-500 border-golden-fizz-500 hover:bg-golden-fizz-600 hover:border-golden-fizz-600 focus:outline-none focus:bg-golden-fizz-600 focus:border-golden-fizz-600 border-2']"
                            >
                                {{ programPackage.booked_out_at ? $t('eventDetails.actions.joinWaitList') : $t('eventDetails.actions.registerNow') }}
                            </component>
                        </dd>

                        <dt class="font-semibold">
                            {{ $t('eventDetails.headline.specialConditions') }}
                        </dt>
                        <dd>
                            {{ $t('eventDetails.specialConditions.text') }}
                        </dd>

                        <dt
                            v-if="programPackage.event_number"
                            class="font-semibold"
                        >
                            {{ $t('eventDetails.headline.eventNumber') }}
                        </dt>
                        <dd v-if="programPackage.event_number">
                            {{ programPackage.event_number }}
                        </dd>
                    </dl>
                </div>

                <div
                    v-if="programPackage.description"
                    class="space-y-4"
                >
                    <h3 class="text-2xl font-semibold text-deep-teal-700">
                        {{ $t('eventDetails.headline.description') }}
                    </h3>

                    <ShowMore>
                        <div
                            class="prose 2xl:prose-lg max-w-none"
                            v-html="programPackage.description"
                        />
                    </ShowMore>
                </div>
            </div>

            <!-- contributors -->
            <div class="space-y-5 overflow-hidden">
                <div
                    v-if="overviewContributors.length > 0"
                    class="border border-gray-200 rounded-xl"
                >
                    <div class="flex items-center justify-between px-6 py-4 lg:py-6">
                        <span class="text-xl font-semibold lg:text-2xl text-deep-teal-700">
                            {{ $t('eventDetails.headline.contributors') }}
                        </span>

                        <button
                            class="inline-flex items-center text-base font-semibold text-deep-teal-700 hover:text-deep-teal-900 hover:underline focus:underline focus:outline-none focus:text-deep-teal-900"
                            @click="$emit('showAllContributors')"
                        >
                            {{ $t('eventDetails.actions.showAllContributors') }}

                            <ArrowLongRightIcon
                                class="w-5 h-5 ml-1.5"
                                aria-hidden="true"
                            />
                        </button>
                    </div>

                    <ul
                        role="list"
                        class="border-t divide-y divide-gray-200 border-t-gray-200"
                    >
                        <li
                            v-for="(contributor, index) in overviewContributors"
                            :key="index"
                            class="p-6"
                        >
                            <button
                                class="flex items-center space-x-6 text-left outline-none group"
                                @click="displayedContributor = contributor"
                            >
                                <picture
                                    v-if="contributor.profile.image"
                                    class="shrink-0"
                                >
                                    <source
                                        :srcset="contributor.profile.image.glide_avatar_image_url_avif"
                                        type="image/avif"
                                    >
                                    <source
                                        :srcset="contributor.profile.image.glide_avatar_image_url_webp"
                                        type="image/webp"
                                    >
                                    <img
                                        class="h-[4.5rem] w-[4.5rem] object-cover rounded-full"
                                        :src="contributor.profile.image.glide_avatar_image_url_jpg"
                                        :alt="`${$t('eventDetails.contributor.image.alt')} ${contributor.profile.fullname}`"
                                        loading="lazy"
                                    >
                                </picture>
                                <div
                                    v-else
                                    class="flex items-center justify-center shrink-0 h-[4.5rem] w-[4.5rem] bg-white rounded-full border"
                                    aria-hidden="true"
                                >
                                    <UserIcon class="w-12 h-12 text-gray-600" />
                                </div>

                                <div class="space-y-0.5 md:space-y-1">
                                    <div
                                        class="text-base font-semibold leading-tight sm:text-lg text-deep-teal-700 group-hover:underline group-focus:underline"
                                    >
                                        {{ contributor.profile.display_name }}
                                    </div>

                                    <div class="font-light text-gray-700 text-sm/tight">
                                        {{ contributor.profile.company }}
                                    </div>
                                </div>
                            </button>
                        </li>
                    </ul>
                </div>

                <!-- cooperations -->
                <div
                    v-if="mainSponsor && mainSponsor.profile.glide_logo_image_url_webp"
                    class="border border-gray-200 rounded-xl"
                >
                    <div class="flex items-center justify-between px-6 py-4 lg:py-6">
                        <span class="text-xl font-semibold lg:text-2xl text-deep-teal-700">
                            {{ $t('eventDetails.headline.cooperations') }}
                        </span>

                        <button
                            class="inline-flex items-center text-base font-semibold text-deep-teal-700 hover:text-deep-teal-900 hover:underline focus:underline focus:outline-none focus:text-deep-teal-900"
                            @click="$emit('showAllCooperations')"
                        >
                            {{ $t('eventDetails.actions.showAllCooperations') }}

                            <ArrowLongRightIcon
                                class="w-5 h-5 ml-1.5"
                                aria-hidden="true"
                            />
                        </button>
                    </div>

                    <figure class="flex flex-wrap justify-center w-full text-gray-500 border-t p-6 space-y-6">
                        <button
                            class="w-full"
                            type="button"
                            @click="displayedCooperation = mainSponsor"
                        >
                            <img
                                :src="mainSponsor.profile.glide_logo_image_url_webp"
                                :title="mainSponsor.profile.company_name"
                                :alt="`${$t('eventDetails.cooperation.logo.alt')} ${mainSponsor.profile.company_name}`"
                                class="w-full"
                                loading="lazy"
                            >
                        </button>

                        <figcaption>
                            {{ $t(`eventDetails.cooperations.displayCategory.mainSponsoring`) }}
                        </figcaption>
                    </figure>

                    <div class="flex flex-wrap items-center justify-center w-full p-6 space-y-5 border-t bg-deep-teal-700">
                        <span
                            class="text-lg lg:text-xl font-semibold text-center text-white"
                        >
                            {{ $t('eventDetails.cooperations.sponsoring.headline') }}
                        </span>
                        <a
                            class="w-auto inline-flex text-center items-center justify-start font-bold text-base text-white border-2 border-white rounded-3xl hover:bg-white/10 px-5 py-2.5 transition-colors"
                            :href="$t('eventDetails.cooperations.sponsoring.url')"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            {{ $t('eventDetails.cooperations.sponsoring.cta') }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </TabPanel>

    <ContributorModal
        v-if="displayedContributor"
        :contributor="displayedContributor"
        :show="Boolean(displayedContributor)"
        @closed="displayedContributor = null"
    />

    <CooperationModal
        v-if="displayedCooperation"
        :cooperation="displayedCooperation"
        :show="Boolean(displayedCooperation)"
        @closed="displayedCooperation = null"
    />
</template>
